import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import portalApi from '../../../../lib/portalApi';
import { useAuth } from '../../../../context/AuthContext';
import {
  ArrowLeft,
  Award,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  Eye,
  FileText,
  Filter,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  AlertCircle,
  X,
  Target,
  UserCheck,
  Briefcase,
  AlertTriangle,
  Video,
  Share2,
  ExternalLink,
  Edit2,
  Ban,
  FileCheck,
} from 'lucide-react';

export default function AdminMentorDetailPage() {
  const { mentorId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isSuperadmin = useMemo(() => {
    return (user?.roles || []).includes('superadmin') || user?.role === 'superadmin';
  }, [user]);

  // Tab state: 'overview' | 'tasks' | 'startups' | 'documents' | 'meetings' | 'activity'
  const activeTab = useMemo(() => {
    const t = searchParams.get('tab') || 'overview';
    if (['overview', 'tasks', 'work', 'startups', 'documents', 'meetings', 'activity'].includes(t)) {
      return t === 'work' ? 'tasks' : t;
    }
    return 'overview';
  }, [searchParams]);

  const setTab = useCallback(
    (tab) => {
      setSearchParams({ tab });
    },
    [setSearchParams]
  );

  // Data states
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [actionError, setActionError] = useState('');

  // Assign Work Modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({
    kind: 'Task',
    title: '',
    description: '',
    priority: 'Medium',
    period: 'monthly',
    target: '',
    unit: 'sessions',
    dueDate: '',
  });
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [assignError, setAssignError] = useState('');

  // Task list filters
  const [taskKindFilter, setTaskKindFilter] = useState('all'); // 'all' | 'Task' | 'KRA' | 'KPI'
  const [taskStatusFilter, setTaskStatusFilter] = useState('all'); // 'all' | 'To Do' | 'In Progress' | 'On Hold' | 'In Review' | 'Done'
  const [taskSortOrder, setTaskSortOrder] = useState('asc'); // 'asc' | 'desc' by dueDate

  // Assign Startup state
  const [availableStartups, setAvailableStartups] = useState([]);
  const [loadingAvailableStartups, setLoadingAvailableStartups] = useState(false);
  const [availableStartupsError, setAvailableStartupsError] = useState('');
  const [selectedStartupToAssign, setSelectedStartupToAssign] = useState('');
  const [assigningStartup, setAssigningStartup] = useState(false);

  // ─── DOCUMENTS SHARING STATE ───────────────────────────────────────────────
  const [selectedDocStartupId, setSelectedDocStartupId] = useState('');
  const [startupDocs, setStartupDocs] = useState([]);
  const [docTotalCount, setDocTotalCount] = useState(0);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [docSearch, setDocSearch] = useState('');
  const [docError, setDocError] = useState('');
  const [docPage, setDocPage] = useState(1);
  const [docTotalPages, setDocTotalPages] = useState(1);
  const [sharingDocId, setSharingDocId] = useState('');
  const activeDocReqRef = useRef(0);

  // ─── MEETINGS MANAGEMENT STATE ─────────────────────────────────────────────
  const [meetings, setMeetings] = useState([]);
  const [loadingMeetings, setLoadingMeetings] = useState(false);
  const [meetingFilter, setMeetingFilter] = useState('all'); // 'all' | 'Upcoming' | 'Past' | 'Cancelled'
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    scope: 'mentor_only', // 'mentor_only' | 'startup'
    startupId: '',
    title: '',
    description: '',
    date: '',
    time: '14:00',
    durationHours: 1,
    meetingLink: '',
    timezone: 'Asia/Kolkata',
  });
  const [schedulingMeeting, setSchedulingMeeting] = useState(false);
  const [scheduleError, setScheduleError] = useState('');

  // Reschedule Meeting State
  const [editMeeting, setEditMeeting] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({
    title: '',
    date: '',
    time: '',
    durationHours: 1,
    meetingLink: '',
    description: '',
  });
  const [reschedulingMeeting, setReschedulingMeeting] = useState(false);
  const [rescheduleError, setRescheduleError] = useState('');

  const loadData = useCallback(async () => {
    if (!mentorId) return;
    setLoading(true);
    setError('');
    try {
      const res = await portalApi.getAdminMentorDetail(mentorId);
      setData(res);
      if (res?.assignedStartups?.[0] && !selectedDocStartupId) {
        setSelectedDocStartupId(res.assignedStartups[0]._id);
      }
    } catch (err) {
      console.error('Failed to load mentor detail:', err);
      setError(err.message || 'Mentor not found or unauthorized');
    } finally {
      setLoading(false);
    }
  }, [mentorId, selectedDocStartupId]);

  const loadAvailableStartups = useCallback(async () => {
    setLoadingAvailableStartups(true);
    setAvailableStartupsError('');
    try {
      const res = await portalApi.listStartups();
      const list = Array.isArray(res) ? res : (res?.startups || []);
      const normalized = list.map((s) => ({
        _id: s.id || s._id,
        id: s.id || s._id,
        name: s.name,
        founderEmail: s.founderEmail || '',
        founderName: s.founderName || s.founderEmail || '',
        stage: s.stage || s.lifecycleStage || '',
        batchId: s.batchId || null,
        sector: s.sector || '',
      }));
      setAvailableStartups(normalized);
    } catch (err) {
      console.error('Failed to load startups for assignment:', err);
      setAvailableStartupsError(err.message || 'Failed to load startups directory');
      setAvailableStartups([]);
    } finally {
      setLoadingAvailableStartups(false);
    }
  }, []);

  const loadStartupDocs = useCallback(
    async (sid, searchStr = '', page = 1) => {
      const reqId = ++activeDocReqRef.current;
      if (!sid || !mentorId) {
        setStartupDocs([]);
        setDocTotalCount(0);
        setDocTotalPages(1);
        setLoadingDocs(false);
        setDocError('');
        return;
      }

      setLoadingDocs(true);
      setStartupDocs([]); // Clear immediately so previous startup documents never linger
      setDocError('');

      try {
        const res = await portalApi.getAdminMentorDocuments(mentorId, {
          startupId: sid,
          search: searchStr ? searchStr.trim() : undefined,
          page,
          limit: 50,
        });

        // If user rapidly switched startups or triggered another search, ignore stale response
        if (activeDocReqRef.current !== reqId) return;

        setStartupDocs(res?.documents || []);
        setDocTotalCount(res?.total || (res?.documents || []).length);
        setDocPage(res?.page || page);
        setDocTotalPages(res?.totalPages || 1);
      } catch (err) {
        if (activeDocReqRef.current !== reqId) return;
        console.error('Failed to load startup documents:', err);
        setStartupDocs([]);
        setDocTotalCount(0);
        setDocError(err.message || 'Failed to load documents for this startup');
      } finally {
        if (activeDocReqRef.current === reqId) {
          setLoadingDocs(false);
        }
      }
    },
    [mentorId]
  );

  const loadMeetings = useCallback(async () => {
    if (!mentorId) return;
    setLoadingMeetings(true);
    try {
      const res = await portalApi.getAdminMentorMeetings(mentorId, {
        status: meetingFilter !== 'all' ? meetingFilter : undefined,
      });
      setMeetings(res?.meetings || []);
    } catch (err) {
      console.error('Failed to load mentor meetings:', err);
    } finally {
      setLoadingMeetings(false);
    }
  }, [mentorId, meetingFilter]);

  useEffect(() => {
    loadData();
    loadAvailableStartups();
  }, [loadData, loadAvailableStartups]);

  useEffect(() => {
    if (activeTab === 'documents') {
      if (!selectedDocStartupId) {
        setStartupDocs([]);
        setDocTotalCount(0);
        setLoadingDocs(false);
        return;
      }
      const timer = setTimeout(() => {
        loadStartupDocs(selectedDocStartupId, docSearch, docPage);
      }, 250);
      return () => clearTimeout(timer);
    }
    if (activeTab === 'meetings') {
      loadMeetings();
    }
  }, [activeTab, selectedDocStartupId, docSearch, docPage, loadStartupDocs, loadMeetings]);

  // Handle Assign Work submission
  const handleAssignWork = async (e) => {
    e.preventDefault();
    if (!assignForm.title.trim()) {
      setAssignError('Title is required');
      return;
    }

    if (['KRA', 'KPI'].includes(assignForm.kind) && !isSuperadmin) {
      setAssignError('Superadmin permission required to assign KRA/KPI');
      return;
    }

    setAssignSubmitting(true);
    setAssignError('');
    try {
      await portalApi.adminAssignMentorTask({
        mentorId,
        kind: assignForm.kind,
        title: assignForm.title.trim(),
        description: assignForm.description.trim(),
        priority: assignForm.priority,
        period: assignForm.period,
        target: assignForm.target ? Number(assignForm.target) : null,
        unit: assignForm.unit,
        dueDate: assignForm.dueDate || null,
      });

      setAssignModalOpen(false);
      setAssignForm({
        kind: 'Task',
        title: '',
        description: '',
        priority: 'Medium',
        period: 'monthly',
        target: '',
        unit: 'sessions',
        dueDate: '',
      });
      setActionSuccess(`Successfully assigned ${assignForm.kind} to ${data?.mentor?.name || 'mentor'}`);
      await loadData();
    } catch (err) {
      setAssignError(err.message || 'Failed to assign work');
    } finally {
      setAssignSubmitting(false);
    }
  };

  // Handle Assign Startup to Mentor
  const handleAssignStartup = async (e) => {
    e.preventDefault();
    if (!selectedStartupToAssign) return;
    setAssigningStartup(true);
    setActionError('');
    try {
      await portalApi.adminAssignStartupToMentor(mentorId, selectedStartupToAssign);
      const assignedId = selectedStartupToAssign;
      setSelectedStartupToAssign('');
      setActionSuccess('Startup successfully assigned to mentor. Startup membership does not automatically grant document access; choose which documents to share in the Documents tab.');
      setSelectedDocStartupId(assignedId);
      await Promise.all([loadData(), loadAvailableStartups()]);
    } catch (err) {
      setActionError(err.message || 'Failed to assign startup');
    } finally {
      setAssigningStartup(false);
    }
  };

  // Handle Remove Mentor from Startup
  const handleRemoveStartup = async (startupId, startupName) => {
    if (!window.confirm(`Are you sure you want to unassign ${startupName} from this mentor? This will also revoke this mentor's document access for this startup.`)) {
      return;
    }
    setActionError('');
    try {
      await portalApi.adminRemoveStartupFromMentor(mentorId, startupId);
      setActionSuccess(`Unassigned ${startupName} and revoked document grants`);
      await Promise.all([loadData(), loadAvailableStartups()]);
      if (selectedDocStartupId === startupId) {
        setSelectedDocStartupId('');
        setStartupDocs([]);
      }
    } catch (err) {
      setActionError(err.message || 'Failed to unassign startup');
    }
  };

  // Handle Document Share/Revoke Toggle
  const handleToggleDocShare = async (docId, currentlyShared) => {
    setSharingDocId(docId);
    setActionError('');
    try {
      if (currentlyShared) {
        await portalApi.adminRevokeDocumentFromMentor(docId, mentorId, selectedDocStartupId);
        setActionSuccess('Document access revoked for mentor');
      } else {
        await portalApi.adminShareDocumentWithMentor(docId, mentorId, selectedDocStartupId);
        setActionSuccess('Document successfully shared with mentor (read-only)');
      }
      await loadStartupDocs(selectedDocStartupId, docSearch, docPage);
    } catch (err) {
      setActionError(err.message || 'Failed to update document sharing');
    } finally {
      setSharingDocId('');
    }
  };

  // Handle Schedule Meeting
  const handleScheduleMeeting = async (e) => {
    e.preventDefault();
    if (!scheduleForm.title.trim()) {
      setScheduleError('Title is required');
      return;
    }
    if (!scheduleForm.date) {
      setScheduleError('Meeting date is required');
      return;
    }

    if (scheduleForm.meetingLink && !scheduleForm.meetingLink.startsWith('https://')) {
      setScheduleError('Meeting link must be an HTTPS URL (e.g. https://meet.google.com/...)');
      return;
    }

    setSchedulingMeeting(true);
    setScheduleError('');
    try {
      const scheduledDateTime = new Date(`${scheduleForm.date}T${scheduleForm.time || '14:00'}:00`);

      await portalApi.adminScheduleMeeting(mentorId, {
        title: scheduleForm.title.trim(),
        description: scheduleForm.description.trim(),
        scheduledAt: scheduledDateTime.toISOString(),
        durationHours: Number(scheduleForm.durationHours) || 1,
        meetingLink: scheduleForm.meetingLink.trim(),
        startupId: scheduleForm.scope === 'startup' ? scheduleForm.startupId : null,
        timezone: scheduleForm.timezone || 'Asia/Kolkata',
      });

      setScheduleModalOpen(false);
      setScheduleForm({
        scope: 'mentor_only',
        startupId: '',
        title: '',
        description: '',
        date: '',
        time: '14:00',
        durationHours: 1,
        meetingLink: '',
        timezone: 'Asia/Kolkata',
      });
      setActionSuccess('Meeting scheduled successfully. It is synchronized with the mentor’s Calendar and an in-app notification was sent.');
      await loadMeetings();
      await loadData();
    } catch (err) {
      setScheduleError(err.message || 'Failed to schedule meeting');
    } finally {
      setSchedulingMeeting(false);
    }
  };

  // Handle Reschedule Meeting
  const handleRescheduleMeeting = async (e) => {
    e.preventDefault();
    if (!editMeeting) return;

    if (rescheduleForm.meetingLink && !rescheduleForm.meetingLink.startsWith('https://')) {
      setRescheduleError('Meeting link must be an HTTPS URL');
      return;
    }

    setReschedulingMeeting(true);
    setRescheduleError('');
    try {
      const scheduledDateTime = rescheduleForm.date
        ? new Date(`${rescheduleForm.date}T${rescheduleForm.time || '14:00'}:00`).toISOString()
        : editMeeting.scheduledAt;

      await portalApi.adminUpdateMeeting(mentorId, editMeeting._id, {
        title: rescheduleForm.title.trim() || editMeeting.heading,
        description: rescheduleForm.description.trim(),
        scheduledAt: scheduledDateTime,
        durationHours: Number(rescheduleForm.durationHours) || editMeeting.durationHours,
        meetingLink: rescheduleForm.meetingLink.trim(),
      });

      setEditMeeting(null);
      setActionSuccess('Meeting rescheduled successfully and updated across mentor Calendar.');
      await loadMeetings();
    } catch (err) {
      setRescheduleError(err.message || 'Failed to reschedule meeting');
    } finally {
      setReschedulingMeeting(false);
    }
  };

  // Handle Cancel Meeting
  const handleCancelMeeting = async (meeting) => {
    const reason = window.prompt(
      `Cancel meeting "${meeting.heading}"? Please enter a cancellation reason:`,
      'Schedule conflict'
    );
    if (reason === null) return;

    try {
      await portalApi.adminCancelMeeting(mentorId, meeting._id, reason || 'Cancelled by admin');
      setActionSuccess(`Meeting "${meeting.heading}" has been cancelled. Join action is now disabled.`);
      await loadMeetings();
    } catch (err) {
      setActionError(err.message || 'Failed to cancel meeting');
    }
  };

  // Filtered & sorted official work
  const filteredAssignments = useMemo(() => {
    const list = data?.assignments || [];
    return list
      .filter((a) => {
        const matchKind = taskKindFilter === 'all' || a.kind === taskKindFilter;
        const matchStatus = taskStatusFilter === 'all' || a.status === taskStatusFilter;
        return matchKind && matchStatus;
      })
      .sort((a, b) => {
        const dateA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
        const dateB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
        return taskSortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      });
  }, [data?.assignments, taskKindFilter, taskStatusFilter, taskSortOrder]);

  // Filtered documents
  const filteredDocs = useMemo(() => {
    return startupDocs.filter((d) => {
      const q = docSearch.trim().toLowerCase();
      if (!q) return true;
      const title = (d.heading || d.originalName || d.title || '').toLowerCase();
      const cat = (d.category || d.type || '').toLowerCase();
      return title.includes(q) || cat.includes(q);
    });
  }, [startupDocs, docSearch]);

  if (loading) {
    return (
      <div className="p-16 text-center text-white/40 space-y-3">
        <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#ff8ec5]" />
        <p className="text-sm">Loading mentor details…</p>
      </div>
    );
  }

  if (error || !data?.mentor) {
    return (
      <div className="p-12 text-center rounded-2xl bg-white/[0.02] border border-white/10 space-y-4 max-w-lg mx-auto mt-8">
        <AlertTriangle className="w-12 h-12 mx-auto text-amber-400" />
        <h2 className="text-white font-semibold text-lg">Mentor Not Found</h2>
        <p className="text-white/50 text-sm">
          {error || 'The requested mentor does not exist or you do not have permission to view them.'}
        </p>
        <Link
          to="/admin/mentors"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#d42b92] text-white text-xs font-semibold hover:bg-[#b02279] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Mentors Directory</span>
        </Link>
      </div>
    );
  }

  const { mentor, summary, assignedStartups, activity } = data;
  const initials = (mentor.name || mentor.email || 'M')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="space-y-6">
      {/* Top Breadcrumb & Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link
            to="/admin/mentors"
            className="p-2 rounded-xl border border-white/10 bg-white/[0.02] text-white/60 hover:text-white hover:border-white/20 transition-all text-xs flex items-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Mentors</span>
          </Link>
          <span className="text-white/20">/</span>
          <span className="text-white/80 font-medium text-sm truncate">{mentor.name}</span>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={loadData}
            className="p-2.5 rounded-xl border border-white/10 bg-white/[0.02] text-white/70 hover:text-white hover:border-white/20 transition-all text-sm flex items-center gap-2"
            title="Refresh details"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline text-xs">Refresh</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setScheduleModalOpen(true);
              setScheduleError('');
            }}
            className="px-3.5 py-2 rounded-xl border border-[#d42b92]/40 bg-[#321129]/60 hover:bg-[#321129] text-[#ff8ec5] hover:text-white font-medium text-xs sm:text-sm transition-all flex items-center gap-2"
          >
            <Calendar className="w-4 h-4 text-[#ff8ec5]" />
            <span>Schedule Meeting</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setAssignModalOpen(true);
              setAssignError('');
            }}
            className="px-4 py-2 rounded-xl bg-[#d42b92] hover:bg-[#b02279] text-white font-medium text-xs sm:text-sm transition-all shadow-lg shadow-[#d42b92]/20 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span>Assign Work</span>
          </button>
        </div>
      </div>

      {/* Global Alerts */}
      {actionSuccess && (
        <div className="p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span>{actionSuccess}</span>
          </div>
          <button type="button" onClick={() => setActionSuccess('')} className="hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {actionError && (
        <div className="p-3.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{actionError}</span>
          </div>
          <button type="button" onClick={() => setActionError('')} className="hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Mentor Profile Header Banner */}
      <div className="p-5 sm:p-6 rounded-2xl bg-[#08080A] border border-[#2C2C34] flex flex-col sm:flex-row sm:items-center justify-between gap-5">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#d42b92]/40 to-[#321129] border border-[#d42b92]/50 text-[#ff8ec5] flex items-center justify-center font-bold text-lg shrink-0">
            {initials}
          </div>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-xl sm:text-2xl font-bold text-white">{mentor.name}</h1>
              <span
                className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  mentor.isActive !== false
                    ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                    : 'bg-zinc-500/10 text-zinc-400 border border-zinc-500/20'
                }`}
              >
                {mentor.isActive !== false ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="text-white/50 text-xs sm:text-sm mt-0.5 flex flex-wrap items-center gap-x-4 gap-y-1">
              <span>{mentor.email}</span>
              {mentor.phone && <span>· {mentor.phone}</span>}
              <span>· Joined {new Date(mentor.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        {/* Quick KPI stats in header */}
        <div className="flex items-center gap-3 border-t sm:border-t-0 sm:border-l border-white/10 pt-3 sm:pt-0 sm:pl-6 text-xs">
          <div>
            <div className="text-white/40 text-[11px]">Startups</div>
            <div className="text-white font-bold text-base mt-0.5">{summary.assignedStartupsCount}</div>
          </div>
          <div className="w-px h-8 bg-white/10 mx-1" />
          <div>
            <div className="text-white/40 text-[11px]">Pending Work</div>
            <div className="text-amber-400 font-bold text-base mt-0.5">{summary.pendingTasks}</div>
          </div>
          <div className="w-px h-8 bg-white/10 mx-1" />
          <div>
            <div className="text-white/40 text-[11px]">KRA/KPI Score</div>
            <div className="text-emerald-400 font-bold text-base mt-0.5">{summary.completionPercentage}%</div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation (Internal Order: Overview, Tasks, Assigned Startups, Documents, Meetings, Activity) */}
      <div className="flex items-center gap-1 border-b border-white/10 overflow-x-auto">
        {[
          { id: 'overview', label: 'Overview', icon: Briefcase },
          { id: 'tasks', label: `Tasks / KRA / KPI (${data.assignments?.length || 0})`, icon: Target },
          { id: 'startups', label: `Assigned Startups (${assignedStartups?.length || 0})`, icon: Building2 },
          { id: 'documents', label: 'Documents', icon: FileText },
          { id: 'meetings', label: 'Meetings', icon: Calendar },
          { id: 'activity', label: 'Activity Log', icon: Eye },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-xs font-semibold border-b-2 transition-all whitespace-nowrap ${
                isActive
                  ? 'border-[#d42b92] text-[#ff8ec5] bg-[#d42b92]/5'
                  : 'border-transparent text-white/50 hover:text-white hover:border-white/20'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* ─────────────────────────────────────────────────────────────
          TAB 1: OVERVIEW
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Summary Metric Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <div className="p-4 rounded-xl bg-[#08080A] border border-[#2C2C34]">
              <div className="text-white/40 text-xs flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 text-[#ff8ec5]" />
                <span>Assigned Startups</span>
              </div>
              <div className="text-2xl font-bold text-white mt-2">{summary.assignedStartupsCount}</div>
              <div className="text-[11px] text-white/40 mt-1">Across active cohorts</div>
            </div>

            <div className="p-4 rounded-xl bg-[#08080A] border border-[#2C2C34]">
              <div className="text-white/40 text-xs flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-amber-400" />
                <span>Pending Tasks</span>
              </div>
              <div className="text-2xl font-bold text-white mt-2">{summary.pendingTasks}</div>
              <div className="text-[11px] text-white/40 mt-1">Awaiting completion/review</div>
            </div>

            <div className="p-4 rounded-xl bg-[#08080A] border border-[#2C2C34]">
              <div className="text-white/40 text-xs flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                <span>Completed Work</span>
              </div>
              <div className="text-2xl font-bold text-white mt-2">{summary.completedTasks}</div>
              <div className="text-[11px] text-white/40 mt-1">Official tasks completed</div>
            </div>

            <div className="p-4 rounded-xl bg-[#08080A] border border-[#2C2C34]">
              <div className="text-white/40 text-xs flex items-center gap-1.5">
                <Target className="w-3.5 h-3.5 text-purple-400" />
                <span>KRA / KPI Progress</span>
              </div>
              <div className="text-2xl font-bold text-white mt-2">{summary.completionPercentage}%</div>
              <div className="text-[11px] text-white/40 mt-1">
                {summary.kraKpiDone} of {summary.kraKpiTotal} done
              </div>
            </div>
          </div>

          {/* Assigned Startups Preview */}
          <div className="p-5 rounded-2xl bg-[#08080A] border border-[#2C2C34] space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Building2 className="w-4 h-4 text-[#ff8ec5]" />
                <span>Assigned Startups ({assignedStartups.length})</span>
              </h3>
              <button
                type="button"
                onClick={() => setTab('startups')}
                className="text-xs text-[#ff8ec5] hover:underline"
              >
                Manage Startups →
              </button>
            </div>

            {assignedStartups.length === 0 ? (
              <div className="p-6 text-center text-white/40 text-xs rounded-xl bg-white/[0.01] border border-dashed border-white/10">
                No startups currently assigned to this mentor.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {assignedStartups.map((s) => (
                  <div
                    key={s._id}
                    className="p-3.5 rounded-xl bg-white/[0.02] border border-white/5 flex items-center justify-between gap-3"
                  >
                    <div>
                      <Link
                        to={`/admin/batches/${s.batchId || 'unassigned'}/startups/${s._id}`}
                        className="text-white font-medium text-xs sm:text-sm hover:text-[#ff8ec5] hover:underline transition-colors flex items-center gap-1"
                      >
                        <span>{s.name}</span>
                        <ExternalLink className="w-2.5 h-2.5 text-white/40" />
                      </Link>
                      <div className="text-white/40 text-[11px]">
                        Founder: {s.founderName || s.founderEmail || 'N/A'} · Stage:{' '}
                        {s.lifecycleStage || 'active'}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedDocStartupId(s._id);
                          setTab('documents');
                        }}
                        className="px-2.5 py-1 rounded-lg bg-[#d42b92]/10 hover:bg-[#d42b92]/20 text-[#ff8ec5] text-[11px] font-medium transition-colors flex items-center gap-1"
                      >
                        <FileText className="w-3 h-3" />
                        <span>Docs</span>
                      </button>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/5 text-white/70">
                        {s.sector || 'Cohort'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 2: TASKS / KRA / KPI
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'tasks' && (
        <div className="space-y-5">
          {/* Work Filters */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 rounded-xl bg-[#08080A] border border-[#2C2C34]">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-white/40 mr-1 flex items-center gap-1">
                <Filter className="w-3.5 h-3.5" /> Filter:
              </span>
              <select
                value={taskKindFilter}
                onChange={(e) => setTaskKindFilter(e.target.value)}
                className="px-2.5 py-1.5 rounded-lg bg-black/40 border border-white/10 text-white text-xs focus:outline-none focus:border-[#d42b92]"
              >
                <option value="all">All Categories</option>
                <option value="Task">Tasks</option>
                <option value="KRA">KRAs</option>
                <option value="KPI">KPIs</option>
              </select>

              <select
                value={taskStatusFilter}
                onChange={(e) => setTaskStatusFilter(e.target.value)}
                className="px-2.5 py-1.5 rounded-lg bg-black/40 border border-white/10 text-white text-xs focus:outline-none focus:border-[#d42b92]"
              >
                <option value="all">All Statuses</option>
                <option value="To Do">To Do</option>
                <option value="In Progress">In Progress</option>
                <option value="On Hold">On Hold</option>
                <option value="In Review">In Review</option>
                <option value="Done">Done</option>
              </select>

              <select
                value={taskSortOrder}
                onChange={(e) => setTaskSortOrder(e.target.value)}
                className="px-2.5 py-1.5 rounded-lg bg-black/40 border border-white/10 text-white text-xs focus:outline-none focus:border-[#d42b92]"
              >
                <option value="asc">Due Date (Earliest first)</option>
                <option value="desc">Due Date (Latest first)</option>
              </select>
            </div>

            <button
              type="button"
              onClick={() => {
                setAssignModalOpen(true);
                setAssignError('');
              }}
              className="px-3.5 py-1.5 rounded-lg bg-[#d42b92] text-white font-medium text-xs hover:bg-[#b02279] transition-colors flex items-center justify-center gap-1.5 self-end sm:self-auto"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Assign Work</span>
            </button>
          </div>

          {/* Work List */}
          {filteredAssignments.length === 0 ? (
            <div className="p-12 text-center rounded-2xl bg-white/[0.02] border border-white/10 space-y-3">
              <Target className="w-10 h-10 mx-auto text-white/20" />
              <h3 className="text-white font-medium text-sm">No work matches your filters</h3>
              <p className="text-white/40 text-xs">
                Try resetting your filters or assign new tasks, KRAs, or KPIs to this mentor.
              </p>
            </div>
          ) : (
            <div className="grid gap-3">
              {filteredAssignments.map((a) => (
                <div
                  key={a._id}
                  className="p-4 rounded-xl bg-[#08080A] border border-[#2C2C34] hover:border-white/20 transition-all space-y-2.5 text-xs"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                          a.kind === 'KRA'
                            ? 'bg-purple-500/10 text-purple-300 border border-purple-500/20'
                            : a.kind === 'KPI'
                            ? 'bg-blue-500/10 text-blue-300 border border-blue-500/20'
                            : 'bg-white/5 text-white/60 border border-white/10'
                        }`}
                      >
                        {a.kind}
                      </span>
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                          a.priority === 'Urgent'
                            ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                            : a.priority === 'High'
                            ? 'bg-amber-500/10 text-amber-300 border border-amber-500/20'
                            : 'bg-white/5 text-white/50 border border-white/5'
                        }`}
                      >
                        {a.priority || 'Medium'}
                      </span>
                      <h4 className="text-white font-semibold text-sm">{a.title}</h4>
                    </div>

                    <div className="flex items-center gap-2 self-start sm:self-auto">
                      <span
                        className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${
                          a.status === 'Done'
                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                            : a.status === 'In Progress'
                            ? 'bg-blue-500/10 text-blue-300 border border-blue-500/20'
                            : a.status === 'In Review'
                            ? 'bg-purple-500/10 text-purple-300 border border-purple-500/20'
                            : a.status === 'On Hold'
                            ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                            : 'bg-white/5 text-white/60 border border-white/10'
                        }`}
                      >
                        {a.status}
                      </span>
                    </div>
                  </div>

                  {a.description && (
                    <p className="text-white/60 text-xs leading-relaxed">{a.description}</p>
                  )}

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-white/40 pt-1 border-t border-white/5">
                    {a.target != null && (
                      <span className="text-white/70">
                        Target: <strong>{a.target}</strong> {a.unit || 'units'}
                      </span>
                    )}
                    {a.period && (
                      <span>Period: <strong className="text-white/70 capitalize">{a.period}</strong></span>
                    )}
                    {a.dueDate && (
                      <span>
                        Due Date: <strong className="text-white/70">
                          {new Date(a.dueDate).toLocaleDateString('en-GB', { timeZone: 'UTC' })}
                        </strong>
                      </span>
                    )}
                    <span>Assigned: {new Date(a.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 3: ASSIGNED STARTUPS
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'startups' && (
        <div className="space-y-5">
          {/* Assign Startup Form */}
          <form
            onSubmit={handleAssignStartup}
            className="p-4 rounded-xl bg-[#08080A] border border-[#2C2C34] flex flex-col sm:flex-row items-stretch sm:items-end gap-3 text-xs"
          >
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-white/60 font-medium">
                  Assign a Startup to this Mentor
                </label>
                {availableStartupsError && (
                  <button
                    type="button"
                    onClick={loadAvailableStartups}
                    className="text-[11px] text-[#ff8ec5] hover:underline flex items-center gap-1"
                  >
                    <RefreshCw className="w-3 h-3" />
                    Retry
                  </button>
                )}
              </div>
              <select
                value={selectedStartupToAssign}
                onChange={(e) => setSelectedStartupToAssign(e.target.value)}
                required
                disabled={loadingAvailableStartups || assigningStartup}
                className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white focus:outline-none focus:border-[#d42b92] disabled:opacity-50"
              >
                {loadingAvailableStartups ? (
                  <option value="">Loading startups directory…</option>
                ) : availableStartupsError ? (
                  <option value="">Failed to load startups ({availableStartupsError})</option>
                ) : (
                  <>
                    <option value="">Select a startup from directory…</option>
                    {availableStartups
                      .filter((s) => !(assignedStartups || []).some((as) => String(as._id) === String(s._id)))
                      .map((s) => (
                        <option key={s._id} value={s._id}>
                          {s.name} ({s.founderName || s.founderEmail || 'Founder'})
                        </option>
                      ))}
                    {availableStartups.length > 0 &&
                      availableStartups.filter((s) => !(assignedStartups || []).some((as) => String(as._id) === String(s._id))).length === 0 && (
                        <option value="" disabled>All eligible directory startups are already assigned</option>
                      )}
                  </>
                )}
              </select>
              {availableStartupsError && (
                <p className="text-red-400 text-[11px] mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {availableStartupsError}
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={assigningStartup || !selectedStartupToAssign || loadingAvailableStartups}
              className="px-4 py-2 rounded-xl bg-[#d42b92] text-white font-medium hover:bg-[#b02279] transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              <UserCheck className="w-4 h-4" />
              <span>{assigningStartup ? 'Assigning…' : 'Link Startup'}</span>
            </button>
          </form>

          {/* Startups List */}
          {assignedStartups.length === 0 ? (
            <div className="p-12 text-center rounded-2xl bg-white/[0.02] border border-white/10 space-y-2">
              <Building2 className="w-10 h-10 mx-auto text-white/20" />
              <h3 className="text-white font-medium text-sm">No startups assigned yet</h3>
              <p className="text-white/40 text-xs">
                Use the form above to link a startup cohort company to this mentor.
              </p>
            </div>
          ) : (
            <div className="grid gap-3">
              {assignedStartups.map((s) => (
                <div
                  key={s._id}
                  className="p-4 rounded-xl bg-[#08080A] border border-[#2C2C34] flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-xs"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/admin/batches/${s.batchId || 'unassigned'}/startups/${s._id}`}
                        className="text-white font-semibold text-sm hover:text-[#ff8ec5] hover:underline transition-colors flex items-center gap-1.5"
                      >
                        <span>{s.name}</span>
                        <ExternalLink className="w-3 h-3 text-white/40" />
                      </Link>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/5 text-white/70">
                        {s.sector || 'Cohort'}
                      </span>
                    </div>
                    <div className="text-white/40 text-xs">
                      Founder: {s.founderName || s.founderEmail || 'N/A'} · Stage: {s.lifecycleStage || 'active'}
                    </div>
                  </div>

                  <div className="flex items-center gap-2.5 self-end sm:self-auto">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedDocStartupId(s._id);
                        setTab('documents');
                      }}
                      className="px-3 py-1.5 rounded-lg border border-[#d42b92]/40 bg-[#d42b92]/10 hover:bg-[#d42b92]/20 text-[#ff8ec5] text-xs font-medium transition-colors flex items-center gap-1.5"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      <span>Manage Documents</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveStartup(s._id, s.name)}
                      className="px-3 py-1.5 rounded-lg border border-red-500/20 text-red-400 hover:bg-red-500/10 text-xs font-medium transition-colors flex items-center gap-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>Unassign</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 4: DOCUMENTS SHARING (DELIBERATE SELECTION)
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'documents' && (
        <div className="space-y-5">
          {/* Startup Selector Header */}
          <div className="p-4 rounded-xl bg-[#08080A] border border-[#2C2C34] flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 text-xs">
            <div className="flex-1 max-w-sm">
              <label className="block text-white/60 font-medium mb-1">
                Select Assigned Startup:
              </label>
              <select
                value={selectedDocStartupId}
                onChange={(e) => {
                  const newSid = e.target.value;
                  setSelectedDocStartupId(newSid);
                  setDocPage(1);
                  loadStartupDocs(newSid, docSearch, 1);
                }}
                className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white focus:outline-none focus:border-[#d42b92]"
              >
                <option value="">Choose an assigned startup…</option>
                {assignedStartups.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name} ({s.sector || 'Cohort'})
                  </option>
                ))}
              </select>
            </div>

            {selectedDocStartupId && (
              <div className="relative flex-1 max-w-xs self-end sm:self-auto">
                <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search startup docs…"
                  value={docSearch}
                  onChange={(e) => setDocSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-black/40 border border-white/10 rounded-xl text-white text-xs placeholder-white/40 focus:outline-none focus:border-[#d42b92]"
                />
              </div>
            )}
          </div>

          {/* Documents Content */}
          {assignedStartups.length === 0 ? (
            <div className="p-12 text-center rounded-2xl bg-white/[0.02] border border-white/10 space-y-3">
              <FileText className="w-10 h-10 mx-auto text-white/20" />
              <h3 className="text-white font-medium text-sm">No Startups Assigned</h3>
              <p className="text-white/40 text-xs max-w-sm mx-auto">
                Documents are tied to startup cohorts. Assign a startup to this mentor first to manage document permissions.
              </p>
              <button
                type="button"
                onClick={() => setTab('startups')}
                className="px-4 py-2 rounded-xl bg-[#d42b92] text-white text-xs font-semibold hover:bg-[#b02279] transition-colors"
              >
                Go to Assigned Startups
              </button>
            </div>
          ) : !selectedDocStartupId ? (
            <div className="p-12 text-center rounded-2xl bg-white/[0.02] border border-white/10 space-y-2">
              <FileCheck className="w-10 h-10 mx-auto text-white/20" />
              <h3 className="text-white font-medium text-sm">Select a startup to view its documents.</h3>
              <p className="text-white/40 text-xs">
                Choose one of the mentor’s assigned startups to inspect and share documents.
              </p>
            </div>
          ) : loadingDocs ? (
            <div className="p-12 text-center text-white/40 space-y-2">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-[#ff8ec5]" />
              <p className="text-xs">Loading startup documents…</p>
            </div>
          ) : docError ? (
            <div className="p-12 text-center rounded-2xl bg-white/[0.02] border border-red-500/20 space-y-3">
              <AlertCircle className="w-8 h-8 mx-auto text-red-400" />
              <h3 className="text-white font-medium text-sm">Failed to Load Documents</h3>
              <p className="text-white/40 text-xs max-w-sm mx-auto">{docError}</p>
              <button
                type="button"
                onClick={() => loadStartupDocs(selectedDocStartupId, docSearch, docPage)}
                className="px-4 py-2 rounded-xl bg-white/10 text-white text-xs font-semibold hover:bg-white/20 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : startupDocs.length === 0 ? (
            <div className="p-12 text-center rounded-2xl bg-white/[0.02] border border-white/10 space-y-2">
              <FileText className="w-10 h-10 mx-auto text-white/20" />
              <h3 className="text-white font-medium text-sm">
                {docSearch ? 'No documents match your search in this startup.' : 'No documents uploaded for this startup.'}
              </h3>
              <p className="text-white/40 text-xs">
                {docSearch
                  ? 'Try adjusting your search keywords or clear the search field.'
                  : 'Upload documents to this startup vault to manage mentor access.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs text-white/40 px-1">
                <span>
                  Showing {startupDocs.length} of {docTotalCount} document{docTotalCount === 1 ? '' : 's'}
                </span>
                {docSearch && (
                  <span className="text-[#ff8ec5]">
                    Filtered by: "{docSearch}"
                  </span>
                )}
              </div>
              <div className="grid gap-3">
              {filteredDocs.map((doc) => {
                const isShared = (doc.sharedWithMentorIds || []).some(
                  (m) => String(m) === String(mentorId)
                );
                const isWorking = sharingDocId === doc._id;

                return (
                  <div
                    key={doc._id}
                    className="p-4 rounded-xl bg-[#08080A] border border-[#2C2C34] hover:border-white/20 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-xs"
                  >
                    <div className="space-y-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#ff8ec5] shrink-0" />
                        <span className="text-white font-semibold text-sm truncate">
                          {doc.heading || doc.originalName || doc.title || 'Document'}
                        </span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/5 text-white/60">
                          {doc.category || doc.type || 'Document'}
                        </span>
                      </div>
                      <div className="text-white/40 text-[11px] pl-6">
                        Uploaded {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : 'N/A'}
                        {doc.fileSize && <span> · {(doc.fileSize / 1024).toFixed(1)} KB</span>}
                      </div>
                    </div>

                    <div className="flex items-center gap-3 self-end sm:self-auto shrink-0">
                      <span
                        className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${
                          isShared
                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                            : 'bg-zinc-500/10 text-zinc-400 border border-zinc-500/20'
                        }`}
                      >
                        {isShared ? 'Shared with Mentor' : 'Not Shared'}
                      </span>

                      <button
                        type="button"
                        disabled={isWorking}
                        onClick={() => handleToggleDocShare(doc._id, isShared)}
                        className={`px-3.5 py-1.5 rounded-xl font-medium text-xs transition-all flex items-center gap-1.5 ${
                          isShared
                            ? 'border border-red-500/30 text-red-400 hover:bg-red-500/10'
                            : 'bg-[#d42b92] hover:bg-[#b02279] text-white shadow-md shadow-[#d42b92]/20'
                        }`}
                      >
                        <Share2 className="w-3.5 h-3.5" />
                        <span>
                          {isWorking ? 'Updating…' : isShared ? 'Revoke Access' : 'Share with Mentor'}
                        </span>
                      </button>
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 5: MEETINGS WITH CALENDAR SYNCHRONIZATION
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'meetings' && (
        <div className="space-y-5">
          {/* Meetings Filters and Schedule Action */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 rounded-xl bg-[#08080A] border border-[#2C2C34]">
            <div className="flex items-center gap-1.5 text-xs">
              <span className="text-white/40 mr-1 flex items-center gap-1">
                <Filter className="w-3.5 h-3.5" /> Filter:
              </span>
              {['all', 'Upcoming', 'Past', 'Cancelled'].map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setMeetingFilter(f)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    meetingFilter === f
                      ? 'bg-[#d42b92]/20 text-[#ff8ec5] border border-[#d42b92]/40'
                      : 'bg-black/30 text-white/60 border border-white/10 hover:text-white'
                  }`}
                >
                  {f === 'all' ? 'All Meetings' : f}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => {
                setScheduleModalOpen(true);
                setScheduleError('');
              }}
              className="px-3.5 py-2 rounded-xl bg-[#d42b92] text-white font-medium text-xs hover:bg-[#b02279] transition-colors flex items-center justify-center gap-1.5 self-end sm:self-auto shadow-md shadow-[#d42b92]/20"
            >
              <Calendar className="w-3.5 h-3.5" />
              <span>Schedule Meeting</span>
            </button>
          </div>

          {/* Meetings List */}
          {loadingMeetings ? (
            <div className="p-12 text-center text-white/40 space-y-2">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-[#ff8ec5]" />
              <p className="text-xs">Loading meetings…</p>
            </div>
          ) : meetings.length === 0 ? (
            <div className="p-12 text-center rounded-2xl bg-white/[0.02] border border-white/10 space-y-3">
              <Calendar className="w-10 h-10 mx-auto text-white/20" />
              <h3 className="text-white font-medium text-sm">No meetings found</h3>
              <p className="text-white/40 text-xs">
                Schedule a mentor session or startup review meeting. Meetings appear directly on the mentor's Calendar.
              </p>
              <button
                type="button"
                onClick={() => setScheduleModalOpen(true)}
                className="px-4 py-2 rounded-xl bg-[#d42b92] text-white text-xs font-semibold hover:bg-[#b02279] transition-colors mt-2"
              >
                Schedule First Meeting
              </button>
            </div>
          ) : (
            <div className="grid gap-3">
              {meetings.map((m) => {
                const isCancelled = m.status === 'Cancelled';
                const formattedTime = new Date(m.scheduledAt).toLocaleString('en-US', {
                  timeZone: m.timezone || 'Asia/Kolkata',
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                });

                return (
                  <div
                    key={m._id}
                    className={`p-4 sm:p-5 rounded-2xl bg-[#08080A] border transition-all space-y-3 text-xs ${
                      isCancelled
                        ? 'border-red-500/20 opacity-70'
                        : 'border-[#2C2C34] hover:border-white/20'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                              m.startupId
                                ? 'bg-[#d42b92]/10 text-[#ff8ec5] border border-[#d42b92]/20'
                                : 'bg-purple-500/10 text-purple-300 border border-purple-500/20'
                            }`}
                          >
                            {m.startupId ? m.startupId.name : 'Mentor-Only Session'}
                          </span>
                          <h4 className="text-white font-semibold text-sm">{m.heading}</h4>
                        </div>
                        {m.description && (
                          <p className="text-white/60 text-xs">{m.description}</p>
                        )}
                      </div>

                      <div className="flex items-center gap-2 self-start sm:self-auto shrink-0">
                        <span
                          className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${
                            m.status === 'Scheduled'
                              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                              : m.status === 'Completed'
                              ? 'bg-blue-500/10 text-blue-300 border border-blue-500/20'
                              : 'bg-red-500/10 text-red-400 border border-red-500/20'
                          }`}
                        >
                          {m.status}
                        </span>
                      </div>
                    </div>

                    {/* Metadata & Actions */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3 border-t border-white/5 text-[11px] text-white/50">
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                        <span className="text-white/80 font-medium">
                          🕒 {formattedTime} ({m.timezone || 'Asia/Kolkata'})
                        </span>
                        <span>Duration: {m.durationHours || 1} hour(s)</span>
                        {isCancelled && m.cancellationReason && (
                          <span className="text-red-400">
                            Reason: {m.cancellationReason}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-2 self-end sm:self-auto">
                        {/* Join Link: active if not cancelled, disabled if cancelled */}
                        {isCancelled ? (
                          <button
                            disabled
                            className="px-3 py-1.5 rounded-xl border border-white/5 bg-white/[0.02] text-white/30 text-xs cursor-not-allowed"
                          >
                            Join Meeting (Cancelled)
                          </button>
                        ) : m.meetingLink ? (
                          <a
                            href={m.meetingLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3.5 py-1.5 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/20 text-xs font-medium transition-colors flex items-center gap-1.5"
                          >
                            <Video className="w-3.5 h-3.5" />
                            <span>Join Meeting</span>
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : null}

                        {!isCancelled && (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setEditMeeting(m);
                                setRescheduleForm({
                                  title: m.heading,
                                  date: new Date(m.scheduledAt).toISOString().split('T')[0],
                                  time: new Date(m.scheduledAt).toTimeString().slice(0, 5),
                                  durationHours: m.durationHours || 1,
                                  meetingLink: m.meetingLink || '',
                                  description: m.description || '',
                                });
                                setRescheduleError('');
                              }}
                              className="px-3 py-1.5 rounded-xl border border-white/10 text-white/70 hover:text-white text-xs transition-colors flex items-center gap-1"
                            >
                              <Edit2 className="w-3 h-3" />
                              <span>Reschedule</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleCancelMeeting(m)}
                              className="px-3 py-1.5 rounded-xl border border-red-500/20 text-red-400 hover:bg-red-500/10 text-xs transition-colors flex items-center gap-1"
                            >
                              <Ban className="w-3 h-3" />
                              <span>Cancel</span>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 6: ACTIVITY LOG
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'activity' && (
        <div className="space-y-4">
          <div className="p-5 rounded-2xl bg-[#08080A] border border-[#2C2C34] space-y-4">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <Eye className="w-4 h-4 text-[#ff8ec5]" />
              <span>Official Activity & Status History</span>
            </h3>

            {activity.length === 0 ? (
              <div className="p-8 text-center text-white/40 text-xs rounded-xl bg-white/[0.01]">
                No recent activity recorded for this mentor.
              </div>
            ) : (
              <div className="space-y-2">
                {activity.map((act, idx) => (
                  <div
                    key={act.id || idx}
                    className="p-3 rounded-xl bg-white/[0.02] border border-white/5 flex items-center justify-between gap-3 text-xs"
                  >
                    <div className="flex items-center gap-2.5">
                      <span
                        className={`w-2 h-2 rounded-full ${
                          act.type === 'meeting' ? 'bg-[#ff8ec5]' : 'bg-emerald-400'
                        }`}
                      />
                      <span className="text-white font-medium">{act.title}</span>
                    </div>
                    <div className="text-white/40 text-[11px] shrink-0">
                      {act.date ? new Date(act.date).toLocaleDateString() : 'N/A'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          SCHEDULE MEETING MODAL
      ───────────────────────────────────────────────────────────── */}
      {scheduleModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="w-full max-w-lg rounded-2xl bg-[#131318] border border-[#2C2C34] p-6 space-y-5 text-xs shadow-2xl">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <div>
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#ff8ec5]" />
                  <span>Schedule Meeting</span>
                </h3>
                <p className="text-white/50 text-xs mt-0.5">
                  Mentor recipient: <strong className="text-white">{mentor.name}</strong> ({mentor.email})
                </p>
              </div>
              <button
                type="button"
                onClick={() => setScheduleModalOpen(false)}
                className="text-white/40 hover:text-white p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {scheduleError && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{scheduleError}</span>
              </div>
            )}

            <form onSubmit={handleScheduleMeeting} className="space-y-4">
              {/* Meeting Scope Selection */}
              <div>
                <label className="block text-white/60 font-medium mb-1.5">Meeting Scope</label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-center gap-2 p-3 rounded-xl bg-black/40 border border-white/10 cursor-pointer">
                    <input
                      type="radio"
                      name="scope"
                      value="mentor_only"
                      checked={scheduleForm.scope === 'mentor_only'}
                      onChange={() => setScheduleForm({ ...scheduleForm, scope: 'mentor_only', startupId: '' })}
                    />
                    <span className="text-white">Mentor-Only Session</span>
                  </label>
                  <label className="flex items-center gap-2 p-3 rounded-xl bg-black/40 border border-white/10 cursor-pointer">
                    <input
                      type="radio"
                      name="scope"
                      value="startup"
                      checked={scheduleForm.scope === 'startup'}
                      onChange={() => setScheduleForm({ ...scheduleForm, scope: 'startup' })}
                      disabled={assignedStartups.length === 0}
                    />
                    <span className={assignedStartups.length === 0 ? 'text-white/30' : 'text-white'}>
                      Startup Cohort Meeting
                    </span>
                  </label>
                </div>
              </div>

              {/* Startup Selection if startup-scoped */}
              {scheduleForm.scope === 'startup' && (
                <div>
                  <label className="block text-white/60 font-medium mb-1">Select Startup *</label>
                  <select
                    value={scheduleForm.startupId}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, startupId: e.target.value })}
                    required
                    className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white focus:outline-none focus:border-[#d42b92]"
                  >
                    <option value="">Select startup…</option>
                    {assignedStartups.map((s) => (
                      <option key={s._id} value={s._id}>
                        {s.name} ({s.founderName || s.founderEmail || 'Founder'})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-white/60 font-medium mb-1">Meeting Title *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Weekly Strategy Review or 1-on-1 Mentor Sync"
                  value={scheduleForm.title}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, title: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:border-[#d42b92]"
                />
              </div>

              {/* Date, Time, Duration */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-white/60 font-medium mb-1">Date *</label>
                  <input
                    type="date"
                    required
                    value={scheduleForm.date}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, date: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white focus:outline-none focus:border-[#d42b92]"
                  />
                </div>
                <div>
                  <label className="block text-white/60 font-medium mb-1">Time (24h)</label>
                  <input
                    type="time"
                    value={scheduleForm.time}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, time: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white focus:outline-none focus:border-[#d42b92]"
                  />
                </div>
                <div>
                  <label className="block text-white/60 font-medium mb-1">Duration (Hrs)</label>
                  <input
                    type="number"
                    min="0.5"
                    step="0.5"
                    value={scheduleForm.durationHours}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, durationHours: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white focus:outline-none focus:border-[#d42b92]"
                  />
                </div>
              </div>

              {/* Meeting Link (HTTPS) */}
              <div>
                <label className="block text-white/60 font-medium mb-1">Meeting Link (HTTPS)</label>
                <input
                  type="url"
                  placeholder="https://meet.google.com/xyz or https://zoom.us/..."
                  value={scheduleForm.meetingLink}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, meetingLink: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:border-[#d42b92]"
                />
              </div>

              {/* Agenda / Description */}
              <div>
                <label className="block text-white/60 font-medium mb-1">Agenda / Notes</label>
                <textarea
                  rows={2}
                  placeholder="Key topics to discuss…"
                  value={scheduleForm.description}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, description: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:border-[#d42b92]"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setScheduleModalOpen(false)}
                  className="px-4 py-2 rounded-xl border border-white/10 text-white/70 hover:text-white text-xs font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={schedulingMeeting}
                  className="px-5 py-2 rounded-xl bg-[#d42b92] text-white text-xs font-semibold hover:bg-[#b02279] transition-colors disabled:opacity-50 flex items-center gap-1.5 shadow-lg shadow-[#d42b92]/20"
                >
                  <Calendar className="w-3.5 h-3.5" />
                  <span>{schedulingMeeting ? 'Scheduling…' : 'Schedule Meeting'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          RESCHEDULE MEETING MODAL
      ───────────────────────────────────────────────────────────── */}
      {editMeeting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="w-full max-w-lg rounded-2xl bg-[#131318] border border-[#2C2C34] p-6 space-y-5 text-xs shadow-2xl">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <div>
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <Edit2 className="w-5 h-5 text-[#ff8ec5]" />
                  <span>Reschedule Meeting</span>
                </h3>
                <p className="text-white/50 text-xs mt-0.5">
                  Updating: <strong className="text-white">{editMeeting.heading}</strong>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditMeeting(null)}
                className="text-white/40 hover:text-white p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {rescheduleError && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{rescheduleError}</span>
              </div>
            )}

            <form onSubmit={handleRescheduleMeeting} className="space-y-4">
              <div>
                <label className="block text-white/60 font-medium mb-1">Title</label>
                <input
                  type="text"
                  required
                  value={rescheduleForm.title}
                  onChange={(e) => setRescheduleForm({ ...rescheduleForm, title: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white focus:outline-none focus:border-[#d42b92]"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-white/60 font-medium mb-1">New Date</label>
                  <input
                    type="date"
                    required
                    value={rescheduleForm.date}
                    onChange={(e) => setRescheduleForm({ ...rescheduleForm, date: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white focus:outline-none focus:border-[#d42b92]"
                  />
                </div>
                <div>
                  <label className="block text-white/60 font-medium mb-1">New Time</label>
                  <input
                    type="time"
                    value={rescheduleForm.time}
                    onChange={(e) => setRescheduleForm({ ...rescheduleForm, time: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white focus:outline-none focus:border-[#d42b92]"
                  />
                </div>
                <div>
                  <label className="block text-white/60 font-medium mb-1">Duration (Hrs)</label>
                  <input
                    type="number"
                    min="0.5"
                    step="0.5"
                    value={rescheduleForm.durationHours}
                    onChange={(e) => setRescheduleForm({ ...rescheduleForm, durationHours: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white focus:outline-none focus:border-[#d42b92]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-white/60 font-medium mb-1">Meeting Link (HTTPS)</label>
                <input
                  type="url"
                  value={rescheduleForm.meetingLink}
                  onChange={(e) => setRescheduleForm({ ...rescheduleForm, meetingLink: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white focus:outline-none focus:border-[#d42b92]"
                />
              </div>

              <div>
                <label className="block text-white/60 font-medium mb-1">Agenda / Description</label>
                <textarea
                  rows={2}
                  value={rescheduleForm.description}
                  onChange={(e) => setRescheduleForm({ ...rescheduleForm, description: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white focus:outline-none focus:border-[#d42b92]"
                />
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setEditMeeting(null)}
                  className="px-4 py-2 rounded-xl border border-white/10 text-white/70 hover:text-white text-xs font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={reschedulingMeeting}
                  className="px-5 py-2 rounded-xl bg-[#d42b92] text-white text-xs font-semibold hover:bg-[#b02279] transition-colors disabled:opacity-50"
                >
                  {reschedulingMeeting ? 'Updating…' : 'Save Reschedule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          ASSIGN WORK MODAL (Fixed to this mentor)
      ───────────────────────────────────────────────────────────── */}
      {assignModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="w-full max-w-lg rounded-2xl bg-[#131318] border border-[#2C2C34] p-6 space-y-5 text-xs shadow-2xl">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <div>
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <Award className="w-5 h-5 text-[#ff8ec5]" />
                  <span>Assign Work to Mentor</span>
                </h3>
                <p className="text-white/50 text-xs mt-0.5">
                  Recipient is fixed to: <strong className="text-white">{mentor.name}</strong> ({mentor.email})
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAssignModalOpen(false)}
                className="text-white/40 hover:text-white p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {assignError && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{assignError}</span>
              </div>
            )}

            <form onSubmit={handleAssignWork} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-white/60 font-medium mb-1">Work Kind</label>
                  <select
                    value={assignForm.kind}
                    onChange={(e) => setAssignForm({ ...assignForm, kind: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white focus:outline-none focus:border-[#d42b92]"
                  >
                    <option value="Task">Task (Standard assignment)</option>
                    <option value="KRA" disabled={!isSuperadmin}>
                      KRA {!isSuperadmin ? '(Superadmin only)' : ''}
                    </option>
                    <option value="KPI" disabled={!isSuperadmin}>
                      KPI {!isSuperadmin ? '(Superadmin only)' : ''}
                    </option>
                  </select>
                </div>

                <div>
                  <label className="block text-white/60 font-medium mb-1">Priority</label>
                  <select
                    value={assignForm.priority}
                    onChange={(e) => setAssignForm({ ...assignForm, priority: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white focus:outline-none focus:border-[#d42b92]"
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-white/60 font-medium mb-1">Title *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Conduct startup review session or Mentor 5 batches"
                  value={assignForm.title}
                  onChange={(e) => setAssignForm({ ...assignForm, title: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:border-[#d42b92]"
                />
              </div>

              {['KRA', 'KPI'].includes(assignForm.kind) && (
                <div className="grid grid-cols-3 gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5">
                  <div>
                    <label className="block text-white/60 font-medium mb-1">Period</label>
                    <select
                      value={assignForm.period}
                      onChange={(e) => setAssignForm({ ...assignForm, period: e.target.value })}
                      className="w-full px-2.5 py-1.5 rounded-lg bg-black/40 border border-white/10 text-white text-xs focus:outline-none focus:border-[#d42b92]"
                    >
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-white/60 font-medium mb-1">Target</label>
                    <input
                      type="number"
                      placeholder="e.g. 5"
                      value={assignForm.target}
                      onChange={(e) => setAssignForm({ ...assignForm, target: e.target.value })}
                      className="w-full px-2.5 py-1.5 rounded-lg bg-black/40 border border-white/10 text-white text-xs focus:outline-none focus:border-[#d42b92]"
                    />
                  </div>
                  <div>
                    <label className="block text-white/60 font-medium mb-1">Unit</label>
                    <input
                      type="text"
                      placeholder="e.g. sessions"
                      value={assignForm.unit}
                      onChange={(e) => setAssignForm({ ...assignForm, unit: e.target.value })}
                      className="w-full px-2.5 py-1.5 rounded-lg bg-black/40 border border-white/10 text-white text-xs focus:outline-none focus:border-[#d42b92]"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-white/60 font-medium mb-1">Due Date</label>
                <input
                  type="date"
                  value={assignForm.dueDate}
                  onChange={(e) => setAssignForm({ ...assignForm, dueDate: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white focus:outline-none focus:border-[#d42b92]"
                />
              </div>

              <div>
                <label className="block text-white/60 font-medium mb-1">Description / Notes</label>
                <textarea
                  rows={2}
                  placeholder="Additional instructions or criteria…"
                  value={assignForm.description}
                  onChange={(e) => setAssignForm({ ...assignForm, description: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:border-[#d42b92]"
                />
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setAssignModalOpen(false)}
                  className="px-4 py-2 rounded-xl border border-white/10 text-white/70 hover:text-white text-xs font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={assignSubmitting}
                  className="px-5 py-2 rounded-xl bg-[#d42b92] text-white text-xs font-semibold hover:bg-[#b02279] transition-colors disabled:opacity-50 flex items-center gap-1.5 shadow-lg shadow-[#d42b92]/20"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>{assignSubmitting ? 'Assigning…' : 'Assign to Mentor'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
